import { JcCommonProps, JcUtils } from "../utils/JcUtils";
import * as React from "react";
import { JcBoxProps, JcColumn } from "./JcBox";
import { JcSelectionBar, JcSelectionOption } from "./button/JcSelectionBar";
import { useMemo } from "react";


export interface JcTab<T extends string> extends JcCommonProps {
    readonly id: T;
    readonly content: React.ReactNode;
    readonly label: React.ReactNode;
    readonly disabled?: boolean;
    readonly scrollable?: boolean;
    /**
     * When the tab's state is URL-backed, pass the tab's path here so its header
     * renders as a link (middle-/Cmd-click open it in a new tab). `onTabChange`
     * still fires for plain clicks. Omit for in-place tabs backed by local state.
     */
    readonly href?: string;
}

export interface JcTabsProps<T extends string> extends Omit<JcBoxProps, "children"> {
    readonly tabs: JcTab<T>[];
    readonly activeTab: T;
    readonly onTabChange: (id: T) => void;
}

export const JcTabs = <T extends string,>(props: JcTabsProps<T>): React.ReactElement<JcTabsProps<T>> => {
    const { tabs, activeTab, onTabChange, className, ...otherCommonProps } = props;
    const selectionOptions: JcSelectionOption<T>[] = useMemo(() => tabs.map(tab => ({
        id: tab.id,
        label: tab.label,
        disabled: tab.disabled,
        href: tab.href,
    })), [tabs]);
    const tabsClassName = JcUtils.classNameString([className, "jc-tabs"]);

    return <JcColumn {...otherCommonProps} className={tabsClassName}>
        <JcSelectionBar selectedID={props.activeTab}
            onClick={onTabChange}
            options={selectionOptions} />
        {tabs.map(tab => {
            const tabCommonProps = JcUtils.commonProps(tab, [
                tab.id !== activeTab && "jc-hidden-tab",
                tab.scrollable && "jc-scrollable-tab",
                "jc-tab"
            ])
            return <JcColumn key={tab.id} {...tabCommonProps}>
                {tab.content}
            </JcColumn>
        })}
    </JcColumn>
}
